import { useState, useEffect, useCallback, useRef } from 'react';

import { activityRepository } from '../repositories/ActivityRepository';
import { ActivityItem, ActivityFilters } from '../types/activity';

interface UseRecentActivityState {
  activities: ActivityItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  total: number;
}

interface UseRecentActivityReturn extends UseRecentActivityState {
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export const useRecentActivity = (
  filters?: ActivityFilters
): UseRecentActivityReturn => {
  const [state, setState] = useState<UseRecentActivityState>({
    activities: [],
    loading: true,
    error: null,
    hasMore: false,
    total: 0,
  });

  const activitiesRef = useRef<ActivityItem[]>([]);

  // Keep the ref in sync with the state
  useEffect(() => {
    activitiesRef.current = state.activities;
  }, [state.activities]);

  const fetchActivities = useCallback(
    async (isLoadMore = false) => {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }));

        const result = await activityRepository.getRecentActivities({
          ...filters,
          offset: isLoadMore ? activitiesRef.current.length : 0,
        });

        setState(prev => ({
          ...prev,
          activities: isLoadMore
            ? [...prev.activities, ...result.activities]
            : result.activities,
          total: result.total,
          hasMore: result.hasMore,
          loading: false,
        }));
      } catch (error) {
        setState(prev => ({
          ...prev,
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to fetch activities',
        }));
      }
    },
    [filters]
  );

  const refetch = useCallback(() => fetchActivities(false), [fetchActivities]);
  const loadMore = useCallback(() => fetchActivities(true), [fetchActivities]);

  useEffect(() => {
    fetchActivities(false);
  }, [fetchActivities]);

  return {
    ...state,
    refetch,
    loadMore,
  };
};

export const useListingActivity = (listingId: string, limit = 5) => {
  const [state, setState] = useState<UseRecentActivityState>({
    activities: [],
    loading: true,
    error: null,
    hasMore: false,
    total: 0,
  });

  const fetchActivities = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const result = await activityRepository.getActivitiesForListing(
        listingId,
        limit
      );

      setState(prev => ({
        ...prev,
        activities: result.activities,
        total: result.total,
        hasMore: result.hasMore,
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch listing activities',
      }));
    }
  }, [listingId, limit]);

  const refetch = useCallback(() => fetchActivities(), [fetchActivities]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  return {
    ...state,
    refetch,
    loadMore: async () => {}, // Not implemented for listing activities
  };
};
